import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import io from "socket.io-client";
import { detectIncognito } from "detectincognitojs";
import useHttp from "../hooks/useHttp";
import { getAthorisationSearchParams } from "../utils/getAthorisationSearchParams";
import { currentLang } from "../App";
import { useToken } from "../hooks/useToken";
import { useAutomationEngine } from "../hooks/useAutomationEngine";

const SOCKET_WAIT_LIMIT = 7000;

const useDialogIdValue = (dialogId) => {
  return useMemo(() => {
    if (!dialogId) return null;
    if (typeof dialogId === "object") {
      return dialogId.data?.id ?? dialogId.id ?? null;
    }
    return dialogId;
  }, [dialogId]);
};

const getErrorMessage = (error) =>
  typeof error === "string" ? error : error?.message || error?.code || "";

const getErrorStatus = (error) =>
  typeof error === "object" && error ? error.status : null;

const hasBackendError = (error, field, code) => {
  const errors =
    typeof error === "object" && error
      ? error.data?.errors || error.errors || {}
      : {};
  const fieldErrors = errors?.[field];

  if (Array.isArray(fieldErrors)) return fieldErrors.includes(code);
  return fieldErrors === code;
};

const isUnverifiedSiteError = (error) =>
  hasBackendError(error, "site", "unverified");

const formatErrorReason = (error) => {
  if (isUnverifiedSiteError(error)) {
    return "site unverified";
  }

  const status = getErrorStatus(error);
  const message = getErrorMessage(error);

  if (status && message) return `HTTP ${status}: ${message}`;
  if (status) return `HTTP ${status}`;
  return message;
};

const isInvalidDomainError = (error) => {
  const message = getErrorMessage(error);

  return (
    isUnverifiedSiteError(error) ||
    /incorrect domain|invalid domain|domain_not_allowed|site unverified/i.test(
      message,
    )
  );
};

function patchHistory(wrap) {
  const origPush = window.history.pushState;
  const origReplace = window.history.replaceState;
  window.history.pushState = wrap("pushState");
  window.history.replaceState = wrap("replaceState");
  return () => {
    window.history.pushState = origPush;
    window.history.replaceState = origReplace;
  };
}

const Context = createContext();
export const useChat = () => useContext(Context);

export const Provider = ({ children }) => {
  const widgetId = localStorage.getItem("widgetId");
  const authParams = getAthorisationSearchParams();

  const { token, setToken, updateToken, clearToken } = useToken();

  const [socketInstance, setSocketInstance] = useState(null);
  const [isTokenChanged, setIsTokenChanged] = useState(false);
  const [isWidgetBlocked, setIsWidgetBlocked] = useState(false);
  const [isIncognito, setIsIncognito] = useState(null);
  const [isDialogActive, setDialogActive] = useState(false);
  const [dialogId, setDialogId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [showWidget, setShowWidget] = useState(true);
  const [refreshBite, setRefreshBite] = useState(0);
  const [hideChatButton, setHideChatButton] = useState(false);
  const [hideChatButtonWithoutGetClient, setHideChatButtonWithoutGetClient] =
    useState(false);
  const lastMessageKeyRef = useRef(null);

  const hideStateMetaRef = useRef({
    hideChatButton: {
      keys: new Set(),
      timers: new Map(),
      releaseOnOnline: new Set(),
    },
    hideChatButtonWithoutGetClient: {
      keys: new Set(),
      timers: new Map(),
      releaseOnOnline: new Set(),
    },
  });
  const hideAutoKeyRef = useRef(0);

  const updateHideFlag = useCallback(
    (type, value) => {
      if (type === "hideChatButton") {
        setHideChatButton(value);
      } else if (type === "hideChatButtonWithoutGetClient") {
        setHideChatButtonWithoutGetClient(value);
      }
    },
    [setHideChatButton, setHideChatButtonWithoutGetClient],
  );

  const addHideKey = useCallback(
    (type, key, options = {}) => {
      const meta = hideStateMetaRef.current[type];
      if (!meta) return;
      meta.keys.add(key);
      if (options.releaseOnOnline) {
        meta.releaseOnOnline.add(key);
      }
      updateHideFlag(type, true);
    },
    [updateHideFlag],
  );

  const removeHideKey = useCallback(
    (type, key) => {
      const meta = hideStateMetaRef.current[type];
      if (!meta) return;
      meta.keys.delete(key);
      meta.releaseOnOnline?.delete(key);
      if (!meta.keys.size) {
        updateHideFlag(type, false);
      }
    },
    [updateHideFlag],
  );

  const clearHideKey = useCallback(
    (type, key) => {
      const meta = hideStateMetaRef.current[type];
      if (!meta) return;
      const existing = meta.timers.get(key);
      if (existing) {
        clearTimeout(existing);
        meta.timers.delete(key);
      }
      removeHideKey(type, key);
    },
    [removeHideKey],
  );

  const scheduleHideRelease = useCallback(
    (type, key, delay) => {
      const meta = hideStateMetaRef.current[type];
      if (!meta) return;
      const existing = meta.timers.get(key);
      if (existing) clearTimeout(existing);
      if (delay <= 0) {
        meta.timers.delete(key);
        removeHideKey(type, key);
        return;
      }
      const timer = setTimeout(() => {
        meta.timers.delete(key);
        removeHideKey(type, key);
      }, delay);
      meta.timers.set(key, timer);
    },
    [removeHideKey],
  );

  const makeHideKey = useCallback((type, triggerId) => {
    if (triggerId) return `${type}:${triggerId}`;
    hideAutoKeyRef.current += 1;
    return `${type}:auto-${hideAutoKeyRef.current}`;
  }, []);

  useEffect(() => {
    return () => {
      Object.values(hideStateMetaRef.current).forEach((meta) => {
        meta.timers.forEach(clearTimeout);
        meta.timers.clear();
      });
    };
  }, []);

  const [authWidgetData, fetchAuthWidgetData, authError, , , dialogStatus] =
    useHttp(`widget/${widgetId}?${authParams}`);
  const [staticWidgetData, fetchStaticWidgetData, staticError] = useHttp(
    `widget/${widgetId}`,
  );
  const [, fetchTriggerRequest] = useHttp(`widget/${widgetId}`);

  const [operatorOnline, setOperatorOnline] = useState(
    Boolean(staticWidgetData?.data?.operator_online),
  );

  const lastVisitorStatusRef = useRef(null);
  const isInitializedRef = useRef(false);

  const dialogIdValue = useDialogIdValue(dialogId);

  useEffect(() => {
    if (staticWidgetData?.data?.operator_online != null) {
      setOperatorOnline(Boolean(staticWidgetData.data.operator_online));
    }
  }, [staticWidgetData?.data?.operator_online]);

  useEffect(() => {
    const hasStaticData = !!staticWidgetData?.data;
    const staticErrorMessage = getErrorMessage(staticError);
    const invalidDomainError = [staticError, authError].find(isInvalidDomainError);
    const invalidDomainReason = invalidDomainError
      ? formatErrorReason(invalidDomainError)
      : "";
    const isBlocked =
      isWidgetBlocked ||
      staticErrorMessage === "chat::error.site_blocked" ||
      staticErrorMessage === "chat::error.visitor_blocked" ||
      Boolean(invalidDomainReason);

    const isIframeDisabled = Boolean(
      !hasStaticData ||
      isBlocked ||
      hideChatButton ||
      hideChatButtonWithoutGetClient,
    );

    document.body.classList.toggle("disabled", isIframeDisabled);

    try {
      window.parent?.postMessage(
        {
          type: "streamchat:iframe:set-disabled",
          payload: {
            disabled: isIframeDisabled,
            reason: isBlocked
              ? invalidDomainReason || staticErrorMessage || "chat::error.widget_blocked"
              : hideChatButton
                ? "hideChatButton"
                : hideChatButtonWithoutGetClient
                  ? "hideChatButtonWithoutGetClient"
                  : hasStaticData
                    ? "ready"
                    : "loading",
          },
        },
        "*",
      );
    } catch {
      // ignore cross-origin access or missing parent
    }
  }, [
    isWidgetBlocked,
    authError,
    staticError,
    hideChatButton,
    hideChatButtonWithoutGetClient,
    staticWidgetData?.data,
  ]);

  const fetchAuthorisationData = useCallback(async () => {
    try {
      await fetchAuthWidgetData({
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
        },
      });
    } catch (err) {
      console.error("Authorization error:", err);
    }
  }, [fetchAuthWidgetData]);

  const initializeSocket = useCallback(() => {
    if (socketInstance) {
      socketInstance.off("visitor.status");
      socketInstance.disconnect();
      setSocketInstance(null);
    }

    const localToken = localStorage.getItem("token");
    if (!localToken) return;

    const newSocket = io(process.env.REACT_APP_SOCKET_URL, {
      query: { token: localToken },
      reconnection: true,
      reconnectionDelay: 500,
    });

    newSocket.on("visitor.status", (data) => {
      const status = data?.status;

      setIsWidgetBlocked(data.status === "blocked");

      const prev = lastVisitorStatusRef.current;
      const shouldAuth =
        status !== "blocked" &&
        (!localStorage.getItem("token") || prev === null || prev === "blocked");
      if (shouldAuth) {
        fetchAuthorisationData();
      }
      lastVisitorStatusRef.current = status;
    });

    setSocketInstance(newSocket);
  }, [fetchAuthorisationData]);

  const geoRef = useRef({ cityId: null, countryId: null });

  const geoIds = useMemo(() => {
    const d = authWidgetData?.data;
    const pickId = (v) => {
      if (v == null) return null;
      if (typeof v === "object" && "id" in v) return Number(v.id) || null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };

    return {
      countryId: pickId(d?.geo_country_id),
      cityId: pickId(d?.geo_city_id),
    };
  }, [authWidgetData]);

  useEffect(() => {
    geoRef.current = {
      cityId: geoIds.cityId ?? null,
      countryId: geoIds.countryId ?? null,
    };
  }, [geoIds.cityId, geoIds.countryId]);

  const pendingTriggersRef = useRef(new Map());

  const fireTrigger = useCallback(
    async (dlgId, triggerId, triggerType, attempt = 1) => {
      if (!dlgId || !triggerId) return;
      try {
        await fetchTriggerRequest({
          method: "POST",
          addUrl: `/dialog/${dlgId}/trigger/${triggerId}`,
          skipSetData: true,
          headers: { Authorization: `Bearer ${token}` },
        });

        if (triggerType === "sendForm") {
          try {
            const event = new CustomEvent("streamchat:open-request-form", {
              detail: { triggerId },
            });
            window.dispatchEvent(event);
          } catch (e) {
            console.error("Failed to dispatch open-request-form event", e);
          }
        }

      } catch (e) {
        const status = Number(e?.status);
        const isFinal = status >= 400 && status < 500;

        if (!isFinal && attempt < 3) {
          const delay = 200 * Math.pow(2, attempt - 1);
          setTimeout(
            () => fireTrigger(dlgId, triggerId, triggerType, attempt + 1),
            delay,
          );
        } else {
          console.error("fireTrigger failed:", e);
        }
      }
    },
    [fetchTriggerRequest, token],
  );

  const [pendingVersion, setPendingVersion] = useState(0);
  const [isSocketReady, setSocketReady] = useState(false);
  const [isSocketWaitOver, setSocketWaitOver] = useState(false);

  useEffect(() => {
    setSocketReady(false);

    if (!socketInstance) return;

    const onConnect = () => setSocketReady(true);
    const onDisconnect = () => setSocketReady(false);

    if (socketInstance.connected) setSocketReady(true);

    socketInstance.on("connect", onConnect);
    socketInstance.on("disconnect", onDisconnect);

    return () => {
      socketInstance.off("connect", onConnect);
      socketInstance.off("disconnect", onDisconnect);
    };
  }, [socketInstance]);

  useEffect(() => {
    const timer = setTimeout(() => setSocketWaitOver(true), SOCKET_WAIT_LIMIT);

    return () => clearTimeout(timer);
  }, []);

  const canFireTriggers = isSocketReady || isSocketWaitOver;

  const enqueuePending = useCallback((triggerId, type) => {
    pendingTriggersRef.current.set(triggerId, { type, triggerId });
    setPendingVersion((v) => v + 1);
  }, []);

  const drainPending = useCallback(async () => {
    const id = dialogIdValue;

    if (!id || !canFireTriggers || pendingTriggersRef.current.size === 0) return;

    const entries = Array.from(pendingTriggersRef.current.values());
    pendingTriggersRef.current.clear();

    for (const { triggerId, type } of entries) {
      await fireTrigger(id, triggerId, type);
    }
  }, [dialogIdValue, fireTrigger, canFireTriggers]);

  useEffect(() => {
    drainPending();
  }, [drainPending, pendingVersion, dialogIdValue]);

  const triggers = useMemo(
    () => staticWidgetData?.data?.triggers || [],
    [staticWidgetData?.data?.triggers],
  );

  const actionHandler = useCallback(
    async ({ type, triggerId, _systemMessage, metrics, conditions }) => {
      switch (type) {
        case "sendMessage":
        case "systemMessage":
        case "sendForm": {
          const id = dialogIdValue;
          if (!id || !canFireTriggers) {
            if (!id) {
              const event = new CustomEvent("streamchat:fetch-dialog", {
                detail: {},
              });

              window.dispatchEvent(event);
            }

            enqueuePending(triggerId, type);

            return true;
          }

          await fireTrigger(id, triggerId, type);
          return true;
        }

        case "hideChatButton": {
          const hideType = "hideChatButton";
          const key = makeHideKey(hideType, triggerId);
          const operatorCondition = (conditions ?? []).find(
            (c) => c.type === "operatorOnline",
          );
          if (operatorCondition) {
            const expected = !!operatorCondition.enable;
            const matches =
              operatorCondition.logic === "≠"
                ? expected !== operatorOnline
                : expected === operatorOnline;
            if (!matches) return false;
          }
          const releaseOnOnline =
            operatorCondition &&
            ((operatorCondition.logic === "≠" && operatorCondition.enable) ||
              (operatorCondition.logic !== "≠" && !operatorCondition.enable));
          addHideKey(hideType, key, { releaseOnOnline });

          const timeCondition = (conditions ?? []).find(
            (c) =>
              ["timeOnSite", "timeOnPage", "timeFromCloseChat"].includes(
                c.type,
              ) &&
              (c.logic === "<" || c.logic === "≤"),
          );
          const threshold = timeCondition ? Number(timeCondition.value) : NaN;
          const metricKey = timeCondition?.type
            ? timeCondition.type === "timeFromCloseChat"
              ? "timeFromCloseChat"
              : timeCondition.type === "timeOnPage"
                ? "timeOnPage"
                : "timeOnSite"
            : null;
          const currentTime = Number.isFinite(metrics?.[metricKey])
            ? metrics[metricKey]
            : 0;
          if (Number.isFinite(threshold)) {
            const delay = Math.max(0, threshold - currentTime) * 1000;
            scheduleHideRelease(hideType, key, delay);
          }

          return true;
        }

        case "hideChatButtonWithoutGetClient": {
          const hideType = "hideChatButtonWithoutGetClient";
          const key = makeHideKey(hideType, triggerId);
          const operatorCondition = (conditions ?? []).find(
            (c) => c.type === "operatorOnline",
          );
          if (operatorCondition) {
            const expected = !!operatorCondition.enable;
            const matches =
              operatorCondition.logic === "≠"
                ? expected !== operatorOnline
                : expected === operatorOnline;
            if (!matches) return false;
          }
          const releaseOnOnline =
            operatorCondition &&
            ((operatorCondition.logic === "≠" && operatorCondition.enable) ||
              (operatorCondition.logic !== "≠" && !operatorCondition.enable));
          addHideKey(hideType, key, { releaseOnOnline });
          return true;
        }

        default:
          return false;
      }
    },
    [
      dialogIdValue,
      canFireTriggers,
      enqueuePending,
      fireTrigger,
      addHideKey,
      makeHideKey,
      scheduleHideRelease,
      operatorOnline,
    ],
  );

  const getOperatorOnline = useCallback(() => operatorOnline, [operatorOnline]);

  const getVisitor = useCallback(() => ({ ...geoRef.current }), []);

  const parentOrigin = useMemo(() => {
    if (typeof document === "undefined") return null;
    try {
      const ref = document.referrer;
      return ref ? new URL(ref).origin : null;
    } catch {
      return null;
    }
  }, []);

  const visitorTimezone = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return undefined;
    }
  }, []);

  const hostBridgeAllowedOrigins = useMemo(
    () =>
      [parentOrigin, window?.location?.origin || undefined].filter(Boolean),
    [parentOrigin],
  );

  const engineTimezone = staticWidgetData?.data?.timezone ?? visitorTimezone;

  const engineDeps = useMemo(
    () => ({
      getOperatorOnline,
      getVisitor,
      onAction: actionHandler,
      // не зупиняємо рушій після першої автодії: мають спрацювати всі увімкнені тригери
      stopAfterFirstAction: false,
      timezone: engineTimezone,
      hostBridgeAllowedOrigins,
    }),
    [
      getOperatorOnline,
      getVisitor,
      actionHandler,
      engineTimezone,
      hostBridgeAllowedOrigins,
    ],
  );

  const engineAutomations = useAutomationEngine(triggers, engineDeps);

  useEffect(() => {
    if (!engineAutomations) return;
    if (!Array.isArray(messages) || messages.length === 0) return;

    const isSystem = (msg) =>
      Boolean(msg?.isSystem || msg?.system || msg?.is_system);
    const getTs = (msg) => {
      const val = msg?.created_at || msg?.time || msg?.updated_at;
      if (!val) return null;
      const d = new Date(val);
      const t = d.getTime();
      return Number.isFinite(t) ? t : null;
    };

    const actual = messages.filter((m) => !m?.isHistory);
    const nonSystem = actual.filter((m) => !isSystem(m));
    const base = nonSystem.length ? nonSystem : actual;

    let minTs = null;
    let maxTs = null;
    let maxId = null;
    for (const m of base) {
      const ts = getTs(m);
      if (ts == null) continue;
      if (minTs == null || ts < minTs) minTs = ts;
      if (maxTs == null || ts > maxTs) {
        maxTs = ts;
        maxId = m?.id ?? null;
      }
    }

    if (maxTs == null) return;
    const key = `${maxId ?? ""}:${maxTs}`;
    if (lastMessageKeyRef.current === key) return;
    lastMessageKeyRef.current = key;

    engineAutomations.notifyFirstMessage(minTs ?? maxTs);
    engineAutomations.notifyLastMessage(maxTs);
  }, [engineAutomations, messages]);

  useEffect(() => {
    if (!socketInstance) return;

    const handler = (payload) => {
      const next = !!payload?.operator_online;
      setOperatorOnline(next);
      engineAutomations?.notifyOperatorStatusChanged();
    };

    socketInstance.on("dialog.operator.online", handler);

    return () => {
      socketInstance.off("dialog.operator.online", handler);
    };
  }, [socketInstance, engineAutomations]);

  useEffect(() => {
    if (geoIds.cityId != null || geoIds.countryId != null) {
      engineAutomations?.evaluateRoute();
    }
  }, [engineAutomations, geoIds.cityId, geoIds.countryId]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (typeof window === "undefined") return;

      try {
        const result = await detectIncognito();
        const value = result.isPrivate ? "1" : "0";

        try {
          localStorage.setItem("isIncognito", value);
        } catch (e) {
          console.error(e);
        }

        if (!cancelled) {
          setIsIncognito(Number(result.isPrivate));
        }
      } catch (err) {
        // Some browsers (e.g. Firefox) are not supported by detectIncognito.
        // In that case we just treat the user as non‑incognito without noisy logs.
        const msg = err?.message || "";
        if (!/cannot determine the browser/i.test(msg)) {
          console.warn("detectincognitojs error:", err);
        }

        try {
          localStorage.setItem("isIncognito", "unknown");
        } catch {}

        if (!cancelled) {
          setIsIncognito(0);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (isIncognito === null || isWidgetBlocked || isInitializedRef.current) {
      return;
    }

    if (token && !staticWidgetData?.data && !staticError) {
      return;
    }

    isInitializedRef.current = true;
    fetchAuthorisationData();
  }, [
    isIncognito,
    isWidgetBlocked,
    token,
    staticWidgetData?.data,
    staticError,
    fetchAuthorisationData,
  ]);

  useEffect(() => {
    if (!authError) return;
    setIsWidgetBlocked(true);
    clearToken();
  }, [authError, clearToken]);

  useEffect(() => {
    if (!staticError) return;
    setIsWidgetBlocked(true);
    clearToken();
  }, [staticError, clearToken]);

  useEffect(() => {
    if (!isWidgetBlocked || !socketInstance) return;

    socketInstance.off("visitor.status");
    socketInstance.disconnect();
    setSocketInstance(null);
  }, [isWidgetBlocked, socketInstance]);

  useEffect(() => {
    const newToken = authWidgetData?.data?.token;
    const currentLocal = localStorage.getItem("token");
    if (newToken && newToken !== token && newToken !== currentLocal) {
      const {
        expires,
        refresh,
        refresh_expires,
        id: sc_visitor,
      } = authWidgetData.data;
      localStorage.setItem("token", newToken);
      localStorage.setItem("expires", expires);
      localStorage.setItem("refresh", refresh);
      localStorage.setItem("refresh_expires", refresh_expires);
      localStorage.setItem("sc_visitor", sc_visitor);
      setToken(newToken);
    }
  }, [authWidgetData?.data?.token]);

  useEffect(() => {
    if (!token) return;
    initializeSocket();

    return () => {
      if (socketInstance) {
        socketInstance.off("visitor.status");
        socketInstance.disconnect();
        setSocketInstance(null);
      }
    };
  }, [token]);

  useEffect(() => {
    if (!engineAutomations) return;
    engineAutomations.notifyOperatorStatusChanged();
  }, [engineAutomations, operatorOnline]);

  useEffect(() => {
    if (!operatorOnline) return;
    const types = ["hideChatButton", "hideChatButtonWithoutGetClient"];
    types.forEach((type) => {
      const meta = hideStateMetaRef.current[type];
      if (!meta || !meta.releaseOnOnline?.size) return;
      Array.from(meta.releaseOnOnline).forEach((key) => {
        clearHideKey(type, key);
      });
    });
  }, [operatorOnline, clearHideKey]);

  useEffect(() => {
    const checkTokenExpiration = () => {
      const expires = localStorage.getItem("expires");
      if (isWidgetBlocked || !expires) return;

      const expirationDate = new Date(expires);
      const now = new Date();
      const minutesLeft = (expirationDate - now) / 1000 / 60;

      if (minutesLeft <= 2) updateToken();
    };

    const intervalId = setInterval(checkTokenExpiration, 180000);
    return () => clearInterval(intervalId);
  }, [isWidgetBlocked, updateToken]);

  useEffect(() => {
    if (
      authWidgetData &&
      localStorage.getItem("dialogStatus") === "active" &&
      dialogStatus === "active"
    ) {
      setDialogActive(true);
    }
  }, [authWidgetData, dialogStatus]);

  const appLang = useMemo(() => {
    return (
      staticWidgetData?.data?.languages?.find((lang) => lang === currentLang) ||
      staticWidgetData?.data?.default_language ||
      "uk"
    );
  }, [staticWidgetData]);

  const value = {
    token,
    setToken,
    widgetId,
    socketInstance,
    setSocketInstance,
    isTokenChanged,
    setIsTokenChanged,
    isWidgetBlocked,
    setIsWidgetBlocked,
    initializeSocket,
    dialogId,
    setDialogId,
    messages,
    setMessages,
    isDialogActive,
    isDialogStateReady: Boolean(authWidgetData),
    setDialogActive,
    engineAutomations,
    hideChatButton,
    hideChatButtonWithoutGetClient,
    widgetData: staticWidgetData?.data,
    name: staticWidgetData?.data?.name,
    logotype: staticWidgetData?.data?.logotype,
    copyDisabled: staticWidgetData?.data?.hide_copy,
    titleMessages: staticWidgetData?.data?.default_messages,
    visitorMessages: staticWidgetData?.data?.visitor_messages,
    fetchStaticWidgetData,
    showWidget,
    setShowWidget,
    refreshBite,
    setRefreshBite,
    defaultLang: appLang,
  };

  return <Context.Provider value={value}>{children}</Context.Provider>;
};
